// card #761 — section-judge gating: a booth may only ENTER the sections
// assigned to its judge via Class.sectionJudgeAssignments (score-judge
// roster ids, #759); every other section renders read-only until the
// assigned booth's own sheet for that section is submitted.
//
// The bridge problem this module solves: Class.judgeAssignments (and its
// scribe mirror) are keyed by arena POSITION (C/E/H/M/B) and store a judge
// NAME string — see apps/score-api/src/handlers/daysheet.js's
// mergeJudgeAssignments/judgeNameKey, which is authoritative for this shape.
// Class.sectionJudgeAssignments instead stores a score-judge roster ID
// (GET /judge, card #730's JUDGE table). Nothing persists a position->id
// mapping directly, so this resolves it the same way score-api's reconcile
// does when it builds Class.judgeIds: match the position's judge NAME
// against the show's judge roster (firstName + " " + lastName).

// #542's qualityJudging predates this and only ever gates by POSITION
// (technical hardcoded to C, quality to qualityJudging.qualityPosition).
// sectionJudgeAssignments is a separate, judge-IDENTITY-based mechanism
// (#759) — the two are independent; this module only concerns itself with
// sectionJudgeAssignments.

function judgeNameKey(judge) {
  return [judge?.firstName, judge?.lastName].filter(Boolean).join(" ").trim();
}

// Resolve the score-judge roster id sitting at `position` for this class, by
// matching Class.judgeAssignments[position]'s name against the show's judge
// roster. Returns null if unresolvable (no name assigned, or no matching
// roster row — e.g. the roster hasn't loaded yet, or offline with a stale
// cache).
export function resolveJudgeIdAtPosition(klass, judgeRoster, position) {
  const name = klass?.judgeAssignments?.[position]?.[0];
  if (!name || !judgeRoster?.length) return null;
  const match = judgeRoster.find((j) => judgeNameKey(j) === name);
  return match?.id ?? null;
}

// The only two movement `section` values sectionJudgeAssignments can gate.
// Any other section (undefined, or freestyle's "artistic") is never gated
// by this field — freestyle's technical/artistic split is untouched by #761.
const GATED_SECTIONS = new Set(["technical", "quality"]);

export function hasSectionJudgeAssignments(klass) {
  const a = klass?.sectionJudgeAssignments;
  return Boolean(a && (a.technical || a.quality));
}

/**
 * Is `section` (a movement's `section` field) editable by the scribe/judge
 * sitting at `position` for this class?
 *
 *  - Class has no sectionJudgeAssignments at all (neither key set) → true,
 *    UNCHANGED — every class that predates #759/#761 keeps working exactly
 *    as before (whatever #542's qualityJudging/visibleMovementsFor already
 *    decided is untouched).
 *  - `section` isn't one sectionJudgeAssignments covers → true, never
 *    gated here.
 *  - That section's assignment is unset (null) → true — nobody has been
 *    declared responsible yet, so don't lock anyone out of it.
 *  - Otherwise: true iff the assigned score-judge id resolves to THIS
 *    booth's position.
 */
export function isSectionEditableForBooth({ klass, judgeRoster, position, section }) {
  if (!hasSectionJudgeAssignments(klass)) return true;
  if (!GATED_SECTIONS.has(section)) return true;
  const assignedJudgeId = klass.sectionJudgeAssignments?.[section] ?? null;
  if (!assignedJudgeId) return true;
  const myJudgeId = resolveJudgeIdAtPosition(klass, judgeRoster, position);
  return Boolean(myJudgeId) && myJudgeId === assignedJudgeId;
}

/**
 * The arena position (C/E/H/M/B) whose judge is assigned `section`, if
 * resolvable — used to (a) label a locked section ("awaiting the judge at
 * E") and (b) look up that booth's own ScoreSheet so a locked section can
 * show its submitted values once they exist (#761 AC2), instead of staying
 * blank forever.
 */
export function resolvePositionForSection({ klass, judgeRoster, section }) {
  if (!GATED_SECTIONS.has(section)) return null;
  const assignedJudgeId = klass?.sectionJudgeAssignments?.[section] ?? null;
  if (!assignedJudgeId || !judgeRoster?.length) return null;
  const assignments = klass?.judgeAssignments ?? {};
  for (const position of Object.keys(assignments)) {
    if (resolveJudgeIdAtPosition(klass, judgeRoster, position) === assignedJudgeId) {
      return position;
    }
  }
  return null;
}
